import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import quotationsService from "services/quotations.service"
import type {
	TQuotation,
	TQuotationDetail,
	TCreateQuotationRequest,
	TQuotationComponentOptionsResponse,
	TQuotationComponentSlug,
	TUpdateQuotationConfigurationRequest,
} from "types/quotations.type"
import type {
	TConnectFurnitureRequest,
	TUpdateErefRequest,
	TUpdateErefStatusRequest,
	TUpdateErefCommentRequest,
	TUpdateEcallRequest,
	TCreateEcallElevationRequest,
	TUpdateEcallElevationsRequest,
	TAutoConnectEcallElevationRequest,
	TAutoConnectErefEcallsRequest,
	TUpdateErefConfigurationRequest,
	TUpdateFurnitureCategoryRequest,
	TUpdateFurnitureFixedPriceRequest,
	TUpdateFurnitureRequest,
	TUpdateFurnitureConfigRequest,
	TUpdateFurnitureCommentRequest,
	TUpdateFurnitureStatusRequest,
	TDuplicateFurnitureRequest,
} from "types/elevationReferences.type"

export const useGetQuotations = (drawingId: number) => {
	return useQuery({
		queryKey: ["quotations", drawingId],
		queryFn: () => quotationsService.getQuotations(drawingId),
		select: ({ data }) => data as TQuotation[],
	})
}

export const useGetQuotation = (quotationId?: number) => {
	return useQuery({
		queryKey: ["quotation", quotationId],
		queryFn: () => quotationsService.getQuotation(quotationId!),
		select: ({ data }) => data as TQuotationDetail,
		enabled: !!quotationId,
	})
}

export const useGetComponentOptions = ({
	seriesId,
	components,
	quotationId,
}: {
	seriesId?: number
	components: TQuotationComponentSlug[]
	quotationId?: number
}) => {
	return useQuery({
		queryKey: ["quotationComponentOptions", seriesId, components.join(","), quotationId],
		queryFn: () =>
			quotationsService.getComponentOptions({
				series_id: seriesId!,
				components,
				quotation_id: quotationId,
			}),
		select: ({ data }) => data as TQuotationComponentOptionsResponse,
		enabled: !!seriesId && components.length > 0,
	})
}

export const useCreateQuotation = () => {
	return useMutation({
		mutationKey: ["createQuotation"],
		mutationFn: (body: TCreateQuotationRequest) => quotationsService.createQuotation(body),
	})
}

export const useUpdateQuotationConfiguration = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: ["updateQuotationConfiguration"],
		mutationFn: ({ quotationId, body }: { quotationId: number; body: TUpdateQuotationConfigurationRequest }) =>
			quotationsService.updateQuotationConfiguration(quotationId, body),
		onSuccess: (response, { quotationId }) => {
			// The PATCH returns the full updated quotation, so seed the cache directly instead of
			// re-fetching the heavy GET quotation endpoint. useGetQuotation's select reads `.data`,
			// so the cached value must be the raw axios response (same shape getQuotation returns).
			queryClient.setQueryData(["quotation", quotationId], response)
		},
	})
}

export const useRenameQuotation = () => {
	return useMutation({
		mutationKey: ["renameQuotation"],
		mutationFn: ({ quotationId, name }: { quotationId: number; name: string }) =>
			quotationsService.renameQuotation(quotationId, name),
	})
}

export const useConnectFurnitureToEref = () => {
	return useMutation({
		mutationKey: ["connectFurnitureToEref"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TConnectFurnitureRequest
		}) => quotationsService.connectFurnitureToEref(quotationId, erefId, body),
	})
}

export const useDisconnectFurnitureFromEref = () => {
	return useMutation({
		mutationKey: ["disconnectFurnitureFromEref"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TConnectFurnitureRequest
		}) => quotationsService.disconnectFurnitureFromEref(quotationId, erefId, body),
	})
}

export const useUpdateErefConfiguration = () => {
	return useMutation({
		mutationKey: ["updateErefConfiguration"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TUpdateErefConfigurationRequest
		}) => quotationsService.updateErefConfiguration(quotationId, erefId, body),
	})
}

export const useUpdateEref = () => {
	return useMutation({
		mutationKey: ["updateEref"],
		mutationFn: ({ quotationId, erefId, body }: { quotationId: number; erefId: number; body: TUpdateErefRequest }) =>
			quotationsService.updateEref(quotationId, erefId, body),
	})
}

export const useUpdateFurnitureCategory = () => {
	return useMutation({
		mutationKey: ["updateFurnitureCategory"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureCategoryRequest
		}) => quotationsService.updateFurnitureCategory(quotationId, furnitureId, body),
	})
}

export const useUpdateFurnitureFixedPrice = () => {
	return useMutation({
		mutationKey: ["updateFurnitureFixedPrice"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureFixedPriceRequest
		}) => quotationsService.updateFurnitureFixedPrice(quotationId, furnitureId, body),
	})
}

export const useUpdateFurniture = () => {
	return useMutation({
		mutationKey: ["updateFurniture"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureRequest
		}) => quotationsService.updateFurniture(quotationId, furnitureId, body),
	})
}

export const useDuplicateFurniture = () => {
	return useMutation({
		mutationKey: ["duplicateFurniture"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TDuplicateFurnitureRequest
		}) => quotationsService.duplicateFurniture(quotationId, furnitureId, body),
	})
}

export const useUpdateFurnitureConfig = () => {
	return useMutation({
		mutationKey: ["updateFurnitureConfig"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureConfigRequest
		}) => quotationsService.updateFurnitureConfig(quotationId, furnitureId, body),
	})
}

export const useUpdateFurnitureComment = () => {
	return useMutation({
		mutationKey: ["updateFurnitureComment"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureCommentRequest
		}) => quotationsService.updateFurnitureComment(quotationId, furnitureId, body),
	})
}

export const useDeleteFurnitureComment = () => {
	return useMutation({
		mutationKey: ["deleteFurnitureComment"],
		mutationFn: ({
			quotationId,
			furnitureId,
			version,
		}: {
			quotationId: number
			furnitureId: number
			version: number
		}) => quotationsService.deleteFurnitureComment(quotationId, furnitureId, version),
	})
}

export const useUpdateErefComment = () => {
	return useMutation({
		mutationKey: ["updateErefComment"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TUpdateErefCommentRequest
		}) => quotationsService.updateErefComment(quotationId, erefId, body),
	})
}

export const useDeleteErefComment = () => {
	return useMutation({
		mutationKey: ["deleteErefComment"],
		mutationFn: ({
			quotationId,
			erefId,
			version,
		}: {
			quotationId: number
			erefId: number
			version: number
		}) => quotationsService.deleteErefComment(quotationId, erefId, version),
	})
}

export const useUpdateFurnitureStatus = () => {
	return useMutation({
		mutationKey: ["updateFurnitureStatus"],
		mutationFn: ({
			quotationId,
			furnitureId,
			body,
		}: {
			quotationId: number
			furnitureId: number
			body: TUpdateFurnitureStatusRequest
		}) => quotationsService.updateFurnitureStatus(quotationId, furnitureId, body),
	})
}

export const useUpdateErefStatus = () => {
	return useMutation({
		mutationKey: ["updateErefStatus"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TUpdateErefStatusRequest
		}) => quotationsService.updateErefStatus(quotationId, erefId, body),
	})
}

export const useUpdateEcall = () => {
	return useMutation({
		mutationKey: ["updateEcall"],
		mutationFn: ({ quotationId, ecallId, body }: { quotationId: number; ecallId: number; body: TUpdateEcallRequest }) =>
			quotationsService.updateEcall(quotationId, ecallId, body),
	})
}

export const useCreateEcallElevation = () => {
	return useMutation({
		mutationKey: ["createEcallElevation"],
		mutationFn: ({
			quotationId,
			ecallId,
			body,
		}: {
			quotationId: number
			ecallId: number
			body: TCreateEcallElevationRequest
		}) => quotationsService.createEcallElevation(quotationId, ecallId, body),
	})
}

export const useDeleteEcallElevation = () => {
	return useMutation({
		mutationKey: ["deleteEcallElevation"],
		mutationFn: ({
			quotationId,
			ecallId,
			ecallElevationId,
		}: {
			quotationId: number
			ecallId: number
			ecallElevationId: number
		}) => quotationsService.deleteEcallElevation(quotationId, ecallId, ecallElevationId),
	})
}

export const useUpdateEcallElevations = () => {
	return useMutation({
		mutationKey: ["updateEcallElevations"],
		mutationFn: ({
			quotationId,
			ecallId,
			body,
		}: {
			quotationId: number
			ecallId: number
			body: TUpdateEcallElevationsRequest
		}) => quotationsService.updateEcallElevations(quotationId, ecallId, body),
	})
}

export const useAutoConnectErefEcalls = () => {
	return useMutation({
		mutationKey: ["autoConnectErefEcalls"],
		mutationFn: ({
			quotationId,
			erefId,
			body,
		}: {
			quotationId: number
			erefId: number
			body: TAutoConnectErefEcallsRequest
		}) => quotationsService.autoConnectErefEcalls(quotationId, erefId, body),
	})
}

export const useAutoConnectEcallElevation = () => {
	return useMutation({
		mutationKey: ["autoConnectEcallElevation"],
		mutationFn: ({
			quotationId,
			ecallId,
			ecallElevationId,
			body,
		}: {
			quotationId: number
			ecallId: number
			ecallElevationId: number
			body: TAutoConnectEcallElevationRequest
		}) => quotationsService.autoConnectEcallElevation(quotationId, ecallId, ecallElevationId, body),
	})
}
